/**
 * Converts a file size in bytes to a human-readable string representation with appropriate units.
 * This utility function automatically selects the most appropriate unit (B, KB, MB, or GB) based on the file size.
 * The function rounds decimal values to two decimal places for better readability.
 *
 * @param sizeInBytes The file size in bytes to be converted to a human-readable format.
 * @returns A formatted string representing the file size with appropriate units (B, KB, MB, or GB).
 *
 * @example
 * ```tsx
 * convertFileSize(512); // Returns "512 B"
 * convertFileSize(2048); // Returns "2.00 KB"
 * convertFileSize(1572864); // Returns "1.50 MB"
 * convertFileSize(2147483648); // Returns "2.00 GB"
 * ```
 */
export const convertFileSize = (sizeInBytes: number) => {
    // eslint-disable-next-line @nfq/no-magic-numbers
    if (sizeInBytes < 1024) {
        return `${sizeInBytes} B`;
    // eslint-disable-next-line @nfq/no-magic-numbers
    } else if (sizeInBytes < 1048576) {
        // eslint-disable-next-line @nfq/no-magic-numbers
        return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    // eslint-disable-next-line @nfq/no-magic-numbers
    } else if (sizeInBytes < 1073741824) {
        // eslint-disable-next-line @nfq/no-magic-numbers
        return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
    }

    // eslint-disable-next-line @nfq/no-magic-numbers
    return `${(sizeInBytes / 1073741824).toFixed(2)} GB`;
};